import { Book, MapPin, Briefcase, ChevronDown, Pencil, Check } from 'lucide-react';
import { useTheme } from '../../../hooks/useTheme';
import SubStepBreadcrumb from './steps/custom/SubStepBreadcrumb';

// Review step for the Smart JD creation flow.
// Pure presentational — all state and handlers come from the parent (CreateJDSmartView).
// Mirrors the same pattern as SharedStep1_JobDetails.


// ─── Sub-components ────────────────────────────────────────────────────────────

export const NavigationButtons = ({ onBack, onNext, nextLabel = 'Next', isLoading = false }) => {
  const { theme } = useTheme();
  return (
    <div className="flex justify-between items-center w-full py-4">
      <button
        onClick={onBack}
        disabled={isLoading}
        className={`${theme !== 'dark' ? 'border-btn !text-textSecondary' : 'button'} flex items-center gap-2 rounded-full dark:!text-white dark:border dark:border-gray-600 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        <span>Back</span>
      </button>
      <button
        onClick={onNext}
        disabled={isLoading}
        className="button px-6 py-3 rounded-full text-white transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
      >
        <span>{isLoading ? 'Saving...' : nextLabel}</span>
      </button>
    </div>
  );
};


// ─── Main component ─────────────────────────────────────────────────────────────

const DIFFICULTY_STYLES = {
  easy:   { label: "Easy",   className: "bg-green-100  text-green-700  dark:bg-green-900/30  dark:text-green-400" },
  medium: { label: "Medium", className: "bg-amber-100  text-amber-700  dark:bg-amber-900/30  dark:text-amber-400" },
  hard:   { label: "Hard",   className: "bg-red-100    text-red-700    dark:bg-red-900/30    dark:text-red-400"   },
};

const SharedStep4_Review = ({
  formData,
  selectedStyle,
  allSkills,
  skillDifficulties = {},
  isUploadingJD,
  // UI state — owned by parent
  isJDExpanded,
  setIsJDExpanded,
  expandedSections,
  setExpandedSections,
  editingIndex,
  setEditingIndex,
  // handlers
  onBack,
  onNext,
  isNextLoading = false,
  handleChange,
  handleCommitEdit,
  getInterviewTypeLabel,
  showSubStepBreadcrumb = false,
  onSubStepNav,
}) => {
  const { theme } = useTheme();

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  // Gradient SVG reused in both branches
  const gradientDef = (
    <svg width="0" height="0" style={{ position: 'absolute' }}>
      <defs>
        <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#ec4899" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
    </svg>
  );

  // Job role details card — shared by both branches
  const jobRoleCard = (isKnowledge) => (
    <div className="relative">
      <label className="absolute text-sm font-normal dark:text-[#DAC0FF] text-textSecondary -top-1 left-6 dark:bg-[#140F1C] bg-bgSecondary px-1">
        Job Role Details
      </label>
      <div
        className={`w-full dark:glass-effect ${isKnowledge ? 'dark:bg-none' : ''} bg-bgPrimary p-4 rounded-2xl border-2 border-gray-700/50 mt-1 transition-all duration-300`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center justify-center gap-2">
              <Book className="w-6 h-6" style={{ stroke: 'url(#grad1)', fill: 'none' }} fill="none" strokeWidth={2} />
              <span className="dark:text-gray-300 text-textPrimary font-normal">
                {formData.jobRole || 'Not Found'}
              </span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <MapPin className="w-6 h-6" style={{ stroke: 'url(#grad1)', fill: 'none' }} fill="none" strokeWidth={2} />
              <span className="dark:text-gray-300 text-textPrimary font-normal">
                {formData.location || '-'}
              </span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Briefcase className="w-6 h-6" style={{ stroke: 'url(#grad1)', fill: 'none' }} fill="none" strokeWidth={2} />
              <span className="dark:text-gray-300 text-textPrimary font-normal">
                {formData.minExp === 0 && formData.maxExp === 0
                  ? '0 Years'
                  : `${formData.minExp}-${formData.maxExp} Years`}
              </span>
            </div>
            <div className="h-9 border border-l border-gray-500" />
            <div className="flex flex-col gap-1">
              <p className={`${isKnowledge ? 'text-purple-400' : 'dark:text-purple-400 text-purple-600'} font-normal text-sm`}>
                Type of Interview:{' '}
                <span className={`${isKnowledge ? 'dark:text-gray-300 text-textPrimary' : 'dark:text-gray-300 text-textSecondary'} font-normal`}>
                  {getInterviewTypeLabel(selectedStyle)}
                </span>
              </p>
              <p className={`${isKnowledge ? 'text-purple-400' : 'dark:text-purple-400 text-purple-600'} font-normal text-sm`}>
                Interview Duration:{' '}
                <span className={`${isKnowledge ? 'dark:text-gray-300 text-textPrimary' : 'dark:text-gray-300 text-textSecondary'} font-normal`}>
                  {formData.interview_timelimit} minutes
                </span>
              </p>
            </div>
          </div>
          <button
            className={`${theme === 'dark' ? 'secondary-button px-3 py-2 pr-3.5' : ''} border-btn !px-3 !py-2 !pr-3.5 relative flex items-center gap-2 rounded-full transition-all ${
              isUploadingJD || !formData.jobRole
                ? 'opacity-60 cursor-not-allowed dark:!text-white !text-textSecondary'
                : 'dark:!text-white !text-textSecondary'
            }`}
            onClick={() => setIsJDExpanded(!isJDExpanded)}
          >
            <span>{isJDExpanded ? 'Close' : 'View JD'}</span>
          </button>
        </div>

        {isJDExpanded && formData.generatedJD && (
          <>
            <div className={`w-full border-t ${isKnowledge ? 'border-gray-500' : 'border-gray-600'} mt-4`} />
            <style>{`.jd-scroll::-webkit-scrollbar{display:none}`}</style>
            <p
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
              className={`jd-scroll px-2 py-4 font-normal mt-2 mb-2 dark:text-gray-300 ${isKnowledge ? 'text-textPrimary' : 'text-textSecondary'} text-sm whitespace-pre-wrap break-words max-h-[350px] overflow-y-auto tracking-wide`}
            >
              {`${formData.generatedJD.job_description || formData.responsibilities}\n\n${
                formData.generatedJD?.['Roles & Responsibilities']?.length
                  ? `Roles & Responsibilities:\n${formData.generatedJD['Roles & Responsibilities']
                      .map((item, i) => `${i + 1}. ${item}`)
                      .join('\n')}`
                  : ''
              }`}
            </p>
          </>
        )}

        {isJDExpanded && !formData.generatedJD && (
          <p className={`dark:text-gray-400 ${isKnowledge ? 'text-textPrimary' : 'text-textSecondary'} mt-4`}>
            No job description generated yet.
          </p>
        )}
      </div>
    </div>
  );

  // Custom questions accordion — shared by both branches
  const customQuestionsSection = (isKnowledge) =>
    formData.questions && formData.questions.length > 0 ? (
      <div className="relative">
        <div className="flex gap-2 items-center mb-4">
          <label className={`text-sm dark:text-gray-400 ${isKnowledge ? 'text-textPrimary' : 'text-textSecondary'} whitespace-nowrap`}>
            Your own added questions
          </label>
          <div className="flex-grow bg-gray-700" style={{ height: '0.5px' }} />
        </div>
        <div className={`dark:glass-effect ${isKnowledge ? 'dark:bg-none' : ''} bg-bgPrimary w-full p-4 rounded-xl border border-2 border-gray-700/50`}>
          <div
            className={`space-y-2 overflow-hidden transition-all duration-300 ${
              expandedSections.customQuestions ? 'max-h-[500px]' : 'max-h-32'
            }`}
          >
            {formData.questions.map((question, index) => (
              <p key={index} className={`dark:text-gray-300 ${isKnowledge ? 'text-textPrimary' : 'text-textSecondary'} text-sm font-light`}>
                {index + 1}.{' '}
                {typeof question === 'string' ? question : question.question}
              </p>
            ))}
          </div>
          {formData.questions.length > 4 && (
            <button
              onClick={() => toggleSection('customQuestions')}
              className={`mt-3 text-sm ${isKnowledge ? 'dark:text-purple-400 text-textPrimary' : 'text-purple-400'} hover:text-purple-300 transition-colors underline underline-offset-2`}
            >
              {expandedSections.customQuestions ? 'View Less' : 'View All'}
            </button>
          )}
        </div>
      </div>
    ) : null;


  // ── Render ──────────────────────────────────────────────────────────────────

  if (selectedStyle === 'screening' || selectedStyle === 'custom') {
    return (
      <>
        {gradientDef}
        <div className="w-full px-12 space-y-6">
          <h2 className="text-lg font-light dark:text-white text-textPrimary mb-4">
            Finally, review your selections and confirm the job role
          </h2>
          <div className="flex flex-col gap-6">
            {jobRoleCard(false)}
            {customQuestionsSection(false)}
          </div>
          <NavigationButtons onBack={onBack} onNext={onNext} nextLabel="Create a Job Role" showborderbtn={true} />
        </div>
      </>
    );
  }

  // knowledge path
  return (
    <>
      {gradientDef}
      <div className="w-full px-12 space-y-6">
        {showSubStepBreadcrumb && <SubStepBreadcrumb activeIndex={4} onStepClick={onSubStepNav} />}
        <h2 className="text-lg font-light dark:text-white text-textPrimary">
          Finally, review your selections and confirm the job role
        </h2>
        <div className="flex flex-col gap-6">
          {jobRoleCard(true)}
          {customQuestionsSection(true)}

          <div className="relative">
            <div className="flex gap-2 items-center mb-4">
              <label className="text-sm dark:text-gray-400 text-textPrimary whitespace-nowrap">
                These are only samples. The interview will have similar questions
              </label>
              <div className="flex-grow bg-gray-700" style={{ height: '0.5px' }} />
            </div>
            <div className="flex flex-row dark:text-gray-400 text-textPrimary w-fit items-center justify-center space-x-2 pr-4 mb-3">
              <p className="text-xs">(you can edit questions if you want to)</p>
            </div>
            <div className="space-y-3">
              {allSkills.map((skill) => {
                const questions = formData.Pre_Written_Questions?.[skill] || [];
                const isExpanded = expandedSections[skill];

                return (
                  <div
                    key={skill}
                    className="dark:glass-effect dark:bg-none border-1 border-gray-300 dark:border-0 bg-bgSecondary w-full py-1.5 rounded-xl overflow-hidden"
                  >
                    <button
                      className="w-full px-4 py-3 flex justify-between items-center transition-colors"
                      onClick={() =>
                        setExpandedSections((prev) => ({ ...prev, [skill]: !prev[skill] }))
                      }
                    >
                      <div className="flex items-center gap-2">
                        <span className="dark:text-gray-300 text-textPrimary text-sm font-normal">
                          {skill.charAt(0).toUpperCase() + skill.slice(1)}
                        </span>
                        {skillDifficulties[skill] && DIFFICULTY_STYLES[skillDifficulties[skill]] && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DIFFICULTY_STYLES[skillDifficulties[skill]].className}`}>
                            {DIFFICULTY_STYLES[skillDifficulties[skill]].label}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-row items-center justify-center space-x-4 relative">
                        <div className="relative group">
                          <Pencil
                            className="size-4 cursor-pointer dark:text-gray-300 hidden"
                            onClick={() => setEditingIndex()}
                          />
                          <p className="absolute -top-4 -left-12 -translate-x-1/2 hidden group-hover:block text-gray-700 shadow dark:bg-gray-900 dark:text-white bg-white text-xs rounded px-2 py-1 whitespace-nowrap z-50">
                            Edit Questions
                          </p>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 dark:text-gray-400 text-textPrimary transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </div>
                    </button>

                    {isExpanded && questions.length > 0 && (
                      <div className="px-4 pb-4 space-y-4">
                        {questions.map((q, i) => (
                          <div key={`${skill}-${i}`} className="flex flex-row w-full space-x-2 group my-2">
                            <p className="dark:text-gray-400 text-textPrimary text-sm">{i + 1} .</p>
                            {editingIndex !== `${skill}-${i}` ? (
                              <div className="flex flex-row w-[98%] items-center justify-between">
                                <div className="flex flex-col flex-1">
                                  {q.topic && (
                                    <p className="text-xs dark:text-gray-500 text-textSecondary mb-1">
                                      {q.topic}
                                      {q.edited && (
                                        <span className="ml-2 dark:text-purple-400 text-purple-500 text-xs">
                                          · Edited
                                        </span>
                                      )}
                                    </p>
                                  )}
                                  <div className="selectable-report">
                                    <p className="dark:text-gray-400 text-textPrimary text-sm">{q.question}</p>
                                  </div>
                                </div>
                                <Pencil
                                  className="size-4 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity dark:text-gray-300 text-textPrimary"
                                  onClick={() => setEditingIndex(`${skill}-${i}`)}
                                />
                              </div>
                            ) : (
                              <div className="w-[98%] flex flex-row items-center justify-between space-x-2">
                                <div className="flex-1">
                                  {q.subtopic && (
                                    <p className="text-xs dark:text-gray-500 text-textSecondary mb-1">
                                      {q.subtopic}
                                    </p>
                                  )}
                                  <input
                                    type="text"
                                    className="w-full bg-transparent border-2 border-gray-200 rounded px-2 py-1 dark:text-gray-400 text-textPrimary text-sm dark:bg-gray-800 bg-white"
                                    value={q.question}
                                    onChange={(e) => handleChange(e, skill)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleCommitEdit(e.target.value, skill);
                                        setEditingIndex(null);
                                      }
                                    }}
                                    onBlur={(e) => {
                                      handleCommitEdit(e.target.value, skill);
                                      setEditingIndex(null);
                                    }}
                                    autoFocus
                                  />
                                </div>
                                <Check
                                  className="hover:cursor-pointer dark:text-gray-300 text-textPrimary"
                                  onClick={() => setEditingIndex(null)}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <NavigationButtons onBack={onBack} onNext={onNext} nextLabel="Create a Job Role" showborderbtn={true} isLoading={isNextLoading} />
      </div>
    </>
  );
};

export default SharedStep4_Review;
